import { isEmptyDisplayValue } from '../FieldWrapper/isEmptyDisplayValue';
import { UseFormReturn } from 'react-hook-form';
import { formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { IDocumentUrl } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import SystemLinkList from '@molecules/SystemLink/SystemLinkList';
import { splitLinkAddresses } from '@molecules/SystemLink/splitLinkAddresses';
import FieldWrapper from '../FieldWrapper/FieldWrapper';
import { RouteNames } from '@/router/router.types';
import { useFormulaDisplay, useFormulaState, useActivated } from '@molecules/formbuilder/hooks';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';
import { useContext } from 'react';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';

export interface IUrlProps {
    url?: IDocumentUrl;
    formMethods: UseFormReturn<any>;
    isEdit: boolean;
    isNew: boolean;
    fields: Record<string, IFieldElem>;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

const Url = ({ url, formMethods, isEdit, isNew, fields, ensureExternalDataSourceLoaded }: IUrlProps) => {
    const runtime = useContext(DocumentRuntimeContext);
    const { activated } = useActivated();
    const visibility = useFormulaState(url?.visibilityRules, fields, formMethods, isEdit, isNew, activated, {
        fallback: true,
        ensureExternalDataSourceLoaded,
        trace: url
            ? {
                  targetId: url.id || url.key,
                  targetKind: 'field',
                  targetName: url.name || url.key,
                  ruleKind: 'visibility',
              }
            : undefined,
    });
    const display = useFormulaDisplay(url?.displayFormat, fields, formMethods, isEdit, isNew, activated, {
        fallback: url?.displayFormat,
        ensureExternalDataSourceLoaded,
        trace: url
            ? {
                  targetId: url.id || url.key,
                  targetKind: 'field',
                  targetName: `${url.name || url.key} — подпись`,
                  ruleKind: 'formula',
              }
            : undefined,
    });
    const href = useFormulaDisplay(url?.href, fields, formMethods, isEdit, isNew, activated, {
        fallback: url?.href,
        ensureExternalDataSourceLoaded,
        trace: url
            ? {
                  targetId: `${url.id || url.key}:href`,
                  targetKind: 'field',
                  targetName: `${url.name || url.key} — адрес`,
                  ruleKind: 'formula',
              }
            : undefined,
    });

    return href.value && visibility && url ? (
        <div
            className="form-field"
            data-testid={url.id ? `url-${url.id}` : undefined}
            {...formulaTraceAttrs(url.id || url.key, 'field')}
        >
            <FieldWrapper
                isViewMode={!isEdit && !isNew}
                isEmpty={isEmptyDisplayValue(href.value) && isEmptyDisplayValue(display.value)}
                inLineFormat={url?.isValue}
                label={url?.name}
                title={url.viewTitle}
                testid={url.id}
            >
                <SystemLinkList
                    href={
                        url.hrefIsDocId
                            ? splitLinkAddresses(href.value)
                                  .map((id) => RouteNames.DOCUMENT.replace(':id', id))
                                  .join('\n')
                            : href.value
                    }
                    caption={display.value}
                    target={url.isBlank ? '_blank' : '_self'}
                    variant="field"
                    disabled={runtime.mode === 'supportPreview'}
                    routerNavigation={url.hrefIsDocId}
                />
            </FieldWrapper>
        </div>
    ) : (
        <></>
    );
};

export default Url;
